import { useState } from "react";
import imageManifest from "../../data/imageManifest.json";

/**
 * Manifest-driven <picture> (AVIF/WebP + compressed fallback), replacing
 * FallbackImage.jsx. `src` is the same logical path already used in
 * frontmatter/JSX (e.g. "/assets/spenden/wayfarer-2026-hero.png") —
 * generated by `npm run optimize-images` (scripts/optimize-images.js).
 *
 * No manifest entry (image not uploaded to public/ yet, per
 * image-inbox/README.md) renders the site's gradient placeholder
 * synchronously — no network request attempted at all, unlike the old
 * FallbackImage which still fired a request and waited for onError.
 * Pass `hideOnMissing` to render nothing instead, for spots where an
 * absent image should just collapse rather than show a placeholder block.
 */
export default function ResponsiveImage({ src, alt, priority = false, sizes = "100vw", variant = "dark", hideOnMissing = false, className, style, "aria-hidden": ariaHidden }) {
  const [failed, setFailed] = useState(false);
  const entry = src ? imageManifest[src] : undefined;

  if (!entry || failed) {
    return hideOnMissing ? null : <div className={variant === "light" ? "tl-diagonal-light" : "tl-diagonal"} style={style} />;
  }

  const loading = priority ? "eager" : "lazy";
  const fetchPriority = priority ? "high" : "auto";

  return (
    <picture>
      {entry.avif.length > 0 && (
        <source type="image/avif" srcSet={entry.avif.map((v) => `${v.path} ${v.width}w`).join(", ")} sizes={sizes} />
      )}
      {entry.webp.length > 0 && (
        <source type="image/webp" srcSet={entry.webp.map((v) => `${v.path} ${v.width}w`).join(", ")} sizes={sizes} />
      )}
      <img
        src={src}
        alt={alt}
        width={entry.width}
        height={entry.height}
        loading={loading}
        decoding="async"
        fetchpriority={fetchPriority}
        aria-hidden={ariaHidden}
        onError={() => setFailed(true)}
        className={className}
        style={style}
      />
    </picture>
  );
}
